import {
  useContext,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { useHistory } from "react-router-dom";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import Image from "../../components/atoms/image";
import Text from "../../components/atoms/text";
import { ArrowNestedIcon, XIconBlack, minusBlackIcon, plusBlackIcon } from "../../utils/iconList";
import styles from "./productDetailsModal.module.css";

import Radio from "@mui/material/Radio";
import { useConfig } from "context/configContext";
import { AccessbilityViewHeight, useNav } from "context/navContext";
import styled from "styled-components";
import Button from "../../components/atoms/button";
import CustomCheckbox from "../../components/atoms/customCheckbox";
import Loader from "../../components/molecules/loader";
import config from "../../config/config";
import { CartContext } from "../../context/cartContext";
import { DeliveryOptionContext } from "../../context/deliveryOptionContext";
import apiList from "../../services/apiList";
import { callApi } from "../../services/callApi";
import {
  DATA_REFERENCE,
  MOVEMENT_KEY,
  SECTOR_KEY,
  TERMINAL_KEY,
} from "../../utils/Constants";
import isJFK, {
  formatPrice,
  isLoggedIn,
  isTab,
  isValidPrice,
} from "../../utils/helperFunctions";
import parse from "html-react-parser";
import DisclaimerPrompt from "components/organisms/DisclaimerPrompt";
import { getSessionStorage } from "utils/storageUtil";
import ProgressiveImg from "components/atoms/ProgressiveImage";
import { triggerAddToCart } from "utils/cdp/Common";
import {
  getTitleArrayFromPreference,
  getPriceArrayFromPreference,
  getSizeFromPreference,
} from "utils/cdp/Constant";
import { buildSelectedStructure, buildPreferencesStructure,buildSelectedCustomizationStructure, convertCustomisationToPreferenceDetail } from "./preferenceUtils";

function ProductDetailsModal(props) {
  const history = useHistory();
  const useCart = useContext(CartContext);
  const useDelivery = useContext(DeliveryOptionContext);
  const { currency } = useConfig();
  const { accessibilityMode } = useNav();
  const descRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const [addOnes, setAddOnes] = useState([]);
  const [selectedValue, setSelectedValue] = useState({});
  const [selectedPreferences, setSelectedPreferences] = useState([]);
  const [selectedPreferenceDetail, setSelectedPreferenceDetail] = useState([]);
  const [err, setErr] = useState("");
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [hasVariantChanged, setHasVariantChanged] = useState(false);

  const [customizedError, setCustomizedError] = useState(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const [showAgeVerificationPrompt, setShowAgeVerificationPrompt] =
    useState(false);

  const preferencesRefs = useRef({});
  const variantRequestId = useRef(0);
  const [openSubPref, setOpenSubPref] = useState({});
  const [isModalLoading, setIsModalLoading] = useState(true);
  const isProductAPITriggeredRef = useRef(false);

  useEffect(() => {
    if (props.isEdit) {
      cartItemPrefill();
    } else {
      setOpenSubPref({});
    }
  }, [
    props.isEdit,
    props.data,
    props.selectedPreferences,
    props.selectedPreferencesDetails,
  ]);

  const checkDescriptionOverflow = () => {
    const el = descRef.current;
    if (el) {
      const isOverflowing = el.scrollHeight > el.clientHeight;
      setShowReadMore(isOverflowing);
    }
  };

  useLayoutEffect(() => {
    const handleResizeOrScroll = () => {
      requestAnimationFrame(() => {
        checkDescriptionOverflow();
      });
    };

    window.addEventListener("resize", handleResizeOrScroll);
    window.addEventListener("scroll", handleResizeOrScroll);

    handleResizeOrScroll();

    return () => {
      window.removeEventListener("resize", handleResizeOrScroll);
      window.removeEventListener("scroll", handleResizeOrScroll);
    };
  }, []);

  useEffect(() => {
    setShowReadMore(false);
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        checkDescriptionOverflow();
      });
    });

    const node = descRef.current;
    if (node) {
      observer.observe(node, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      requestAnimationFrame(() => checkDescriptionOverflow());
    }

    return () => {
      observer.disconnect();
    };
  }, [props]);

  const computeInitialExpandedKeys = (preferences, preferenceDetail) => {
    const expanded = {};

    const selectedSkus = new Set();
    (preferenceDetail || []).forEach((detail) => {
      (detail.selection || []).forEach((sel) => {
        selectedSkus.add(String(sel.preferenceSku));
      });
    });

    const walk = (items, parentKey) => {
      let anySelected = false;

      (items || []).forEach((item, index) => {
        const key = `${parentKey}-${String(index)}`;
        const hasNested = item?.subPreferences?.length > 0;

        if (hasNested) {
          let childSelected = false;
          item.subPreferences.forEach((subGroup, subIndex) => {
            const nestedKey = `${key}-${subIndex}`;
            if (walk(subGroup?.preferenceValues ?? [], nestedKey)) {
              childSelected = true;
            }
          });
          if (childSelected) {
            expanded[key] = true; 
            anySelected = true;
          }
        } else if (selectedSkus.has(String(item.preferenceSku))) {
          anySelected = true;
        }
      });

      return anySelected;
    };

    (preferences || []).forEach((preferenceItem) => {
      walk(preferenceItem.preferenceValues || [], `${preferenceItem.preferenceId}`);
    });

    return expanded;
  };

  const cartItemPrefill = () => {
    if (props.data && Object.keys(props.data).length > 0) {
      if (props.selectedPreferences) {
        setSelectedPreferences(
          JSON.parse(JSON.stringify(props.selectedPreferences))
        );
      }
      if (props.selectedPreferencesDetails) {
        const fullPreferenceTree = props?.data?.pDetails?.preferences || [];
        const prefDetail = convertCustomisationToPreferenceDetail(
          props.selectedPreferencesDetails,
          fullPreferenceTree
        );
        setSelectedPreferenceDetail(
          JSON.parse(JSON.stringify(prefDetail))
        );

        setOpenSubPref(computeInitialExpandedKeys(fullPreferenceTree, prefDetail));
      }
    }
  };

  function getAttributes(ds) {
    let arr = [];
    Object.keys(ds).map((key) => arr.push({ type: key, value: ds[key] }));
    return arr;
  }

  const handleAttributeSelection = async (type, valueItem, skipLoadingCheck = false) => {
    if (isLoading && !skipLoadingCheck) return;

    const requestId = variantRequestId.current + 1;
    variantRequestId.current = requestId;

    try {
      setIsLoading(true);
      setHasVariantChanged(true);
      let ds = { ...selectedValue };
      let arr = [];
      if (ds[type]) {
        arr = ds[type];
      }
      arr[0] = valueItem.attributeValue;
      ds[type] = arr;
      setSelectedValue(ds);

      let ancestorArray = [];
      if (props?.data?.masterVariants) {
        ancestorArray.push(props?.data?.masterVariants?._id);
      }
      let apiURL = apiList.products.productId;
      let reqBody = {
        shopId: props.data.pDetails.shopId,
        productId: props.data.pDetails._id,
        ancestorsId: props?.data?.masterVariants?._id,
        attributes: getAttributes(ds),
      };

      const response = await callApi.post(apiURL, reqBody);
      if (requestId !== variantRequestId.current) return;

      if (
        !(
          void 0 === response ||
          void 0 === response.data ||
          void 0 === response.data.productId ||
          "" === response.data.productId ||
          " " === response.data.productId
        )
      ) {
        if (props.data.pDetails._id !== response.data.productId) {
          await props.refreshItemData(
            response.data.productId,
            props.data.pDetails.shopId
          );
          if (requestId !== variantRequestId.current) return;

          setSelectedPreferences([]);
          setSelectedPreferenceDetail([]);
        }
      } else {
        console.log("Item Variant not available...");
      }
    } catch (err) {
      console.log(err);
    } finally {
      if (requestId === variantRequestId.current) {
        setIsLoading(false);
        setIsModalLoading(false);
      }
    }
  };

  const checkIfPreferenceExists = (typeId, valueId) => {
    let found = selectedPreferences.find((x) => x.preferenceId === typeId);
    if (found) {
      if (found.values.includes(valueId)) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const handlePreferenceSelection = (
    id,
    type,
    singleSelect,
    valueItem,
    preferenceItem = null,
    parentItem = null,
    isSubPreference = false,
    preferenceQty = 0,
  ) => {
    try {
      if (valueItem?.repeatable && preferenceQty && valueItem?.maxQuantity && preferenceQty > valueItem?.maxQuantity) {
        setCustomizedError(
          `You can choose up to ${valueItem.maxQuantity} quantity for ${valueItem?.name}`
        );
        setTimeout(() => setCustomizedError(""), 3000);
        return;
      }

      const updatedValue = {
        ...valueItem,
        _id: valueItem?._id || valueItem?.preferenceSku,
        isSubPreferenceSelected: valueItem?.isSubPreferenceSelected ?? isSubPreference,
        parentPreferenceSku:
          valueItem?.parentPreferenceSku ?? parentItem?.preferenceSku ?? null,
      };

      let updatedDetails = [...selectedPreferenceDetail];
      let detailIndex = updatedDetails.findIndex((x) => x.title === type);

      if (detailIndex === -1) {
        let foundInNested = false;
        updatedDetails.forEach((d) => {
          (d.selection || []).forEach((item) => {
            (item.subSelections || []).forEach((subGroup) => {
              if (subGroup.title === type) {
                foundInNested = true;
              }
            });
          });
        });

        if (foundInNested) {
          const flatSelection = [];
          updatedDetails.forEach((d) => {
            (d.selection || []).forEach((item) => {
              (item.subSelections || []).forEach((subGroup) => {
                if (subGroup.title === type) {
                  (subGroup.selection || []).forEach((leaf) => {
                    flatSelection.push({
                      preferenceSku: leaf.preferenceSku,
                      name: leaf.name,
                      preferenceDietCategory: leaf.preferenceDietCategory || "",
                      isActive: leaf.isActive ?? true,
                      preferenceOrder: leaf.preferenceOrder || "",
                      isSubPreferenceSelected: true,
                      parentPreferenceSku: null,
                      _id: leaf.preferenceSku,
                    });
                  });
                }
              });
            });
          });

          if (flatSelection.length > 0) {
            updatedDetails.push({
              title: type,
              preferenceId: id,
              selection: flatSelection,
            });
            detailIndex = updatedDetails.length - 1;
          }
        }
      }

      let currentSelection =
        detailIndex > -1 ? [...updatedDetails[detailIndex].selection] : [];

      const existingIndex = currentSelection.findIndex(
        (ele) => String(ele.preferenceSku) === String(updatedValue.preferenceSku)
      );

      const isMaxReached = () => {
        const maxPrefPermitted = preferenceItem?.maxPrefPermitted;
        if (singleSelect || !maxPrefPermitted) return false;

        const countForMax = updatedValue.isSubPreferenceSelected
          ? currentSelection.length
          : currentSelection.filter((s) => !s.isSubPreferenceSelected).length;

        if (countForMax >= maxPrefPermitted) {
          setCustomizedError(`You can choose up to ${maxPrefPermitted} options`);
          setTimeout(() => setCustomizedError(""), 3000);
          return true;
        }
        return false;
      };

      if (preferenceQty > 0) {
        if (existingIndex > -1) {
          currentSelection = updatePreferenceQtyRecursively(
            currentSelection,
            updatedValue.preferenceSku,
            preferenceQty
          );
        } else {
          if (isMaxReached()) return;

          const newItem = { ...updatedValue, quantity: preferenceQty };
          currentSelection = singleSelect ? [newItem] : [...currentSelection, newItem];
        }

        if (detailIndex > -1) {
          updatedDetails[detailIndex] = {
            ...updatedDetails[detailIndex],
            selection: currentSelection,
          };
        } else {
          updatedDetails.push({
            title: type,
            preferenceId: id,
            selection: currentSelection,
          });
        }
      } else {
        const isRemoving = existingIndex > -1;

        if (isRemoving) {
          currentSelection.splice(existingIndex, 1);
        } else {
          if (isMaxReached()) return;

          currentSelection = singleSelect ? [updatedValue] : [...currentSelection, updatedValue];
        }

        if (detailIndex > -1) {
          updatedDetails[detailIndex] = {
            ...updatedDetails[detailIndex],
            selection: currentSelection,
          };
        } else {
          updatedDetails.push({
            title: type,
            preferenceId: id,
            selection: currentSelection,
          });
        }
      }

      updatedDetails = updatedDetails.filter((d) => d.selection.length > 0);
      setSelectedPreferenceDetail(updatedDetails);

      const selectedPreferenceMap = {};
      updatedDetails.forEach((d) => {
        selectedPreferenceMap[d.title] = d.selection;
      });

      const fullPreferenceTree = props?.data?.pDetails?.preferences || [];

      const nestedPreferencesPayload = buildPreferencesStructure(
        selectedPreferenceMap,
        fullPreferenceTree
      );
      console.log("nestedPreferencesPayload", nestedPreferencesPayload)

      const updatedNestedPreferences = buildSelectedStructure(
        nestedPreferencesPayload,
        fullPreferenceTree,
        true
      );
      console.log("updatedNestedPreferences", updatedNestedPreferences)
      setSelectedPreferences(updatedNestedPreferences);
    } catch (err) {
      console.log(err);
    }
  };

  const updatePreferenceQtyRecursively = (items, sku, qty) => {
    return items.map((item) => {
      if (String(item.preferenceSku) === String(sku)) {
        return { ...item, quantity: qty };
      }
      return item;
    });
  };


  const style = {
    position: "absolute",
    top: accessibilityMode ? "80vh" : "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isTab() ? "calc(100% - 60px)" : "954px",
    maxHeight: accessibilityMode
      ? `calc(${AccessbilityViewHeight} - 4vh)`
      : "unset",
    overflowY: accessibilityMode ? "auto" : "unset",
    border: "none",
    borderRadius: "12px",
    scrollbarWidth: "none",
  };

  useEffect(() => {
    if (props.data && Object.keys(props.data).length > 0) {
      if (Object.keys(selectedValue).length === 0 && props.defaults) {
        setSelectedValue(props.defaults);
      }
      if (selectedPreferences.length === 0 && props.selectedPreferences) {
        setSelectedPreferences(
          JSON.parse(JSON.stringify(props.selectedPreferences))
        );
      }
      if (
        selectedPreferenceDetail.length === 0 &&
        props.selectedPreferencesDetails
      ) {
        const fullPreferenceTree = props?.data?.pDetails?.preferences || [];
        const prefDetail = JSON.parse(
          JSON.stringify(props.selectedPreferencesDetails)
        );
        setSelectedPreferenceDetail(prefDetail);

        setOpenSubPref((prev) => ({
          ...prev,
          ...computeInitialExpandedKeys(fullPreferenceTree, prefDetail),
        }));
      }
      setAddOnes(props.data.pDetails.addons);
    }
  }, [
    props.data,
    props.defaults,
    props.selectedPreferences,
    props.selectedPreferencesDetails,
  ]);

  useEffect(() => {
    if (Object.keys(selectedValue).length === 0 && Object.keys(props.defaults).length > 0 && props?.caller == "productlist" && props?.data?.masterVariants?.productVariants?.length > 0) {
      if (isProductAPITriggeredRef.current) return;
      isProductAPITriggeredRef.current = true;
      setSelectedValue(props.defaults);
      fetchSelectedMastervariant(props.defaults);
    } else {
      setIsModalLoading(false);
    }
  }, [props.defaults]);

  const fetchSelectedMastervariant = (defaults) => {
    props?.data?.masterVariants?.productVariants?.forEach((variantItem) => {
      const seen = new Set();
      const matchedValues = variantItem?.attributeValues?.filter((valueItem) => {
        const isMatch = defaults?.[variantItem.attributeName]?.includes(
          valueItem.attributeValue
        );
        if (isMatch && !seen.has(valueItem.attributeValue)) {
          seen.add(valueItem.attributeValue);
          return true;
        }
        return false;
      });

      if (matchedValues && matchedValues.length > 0) {
        matchedValues.forEach((valueItem) => {
          handleAttributeSelection(variantItem.attributeName, valueItem, true);
        });
      } else {
        setIsModalLoading(false)
      }
    });
  };

  const getProductImage = () => {
    if (
      props.data.pDetails &&
      props.data.pDetails.productImageUrl &&
      props.data.pDetails.productImageUrl instanceof Array &&
      props.data.pDetails.productImageUrl.length > 0
    ) {
      const found = props.data.pDetails.productImageUrl.find(
        (x) =>
          x?.type?.toLowerCase() ===
          config?.imageType?.product?.toLowerCase() &&
          x?.isActive?.toLowerCase() === "y"
      );
      if (found) {
        return found?.imageUrl || "";
      }
    } else {
      return "";
    }
  };

  const handleModalClose = () => {
    props.setOpenDetailsModal(false);
    props.setSelectedProductsDetails({});
    if (props.setDefaults) {
      props.setDefaults({});
    }
    setSelectedValue({});
    setHasVariantChanged(false);
    setSelectedPreferences([]);
    setSelectedPreferenceDetail([]);
    setCustomizedError(null);
    if (props.reRenderSetter) {
      props.reRenderSetter(!props.reRenderFlag);
    }
  };

  const handleAddItem = () => {
    const item = props?.item;
    const shop = props?.shopDetails;
    const rawId = props.data.pDetails._id || "";
    const cleanId = rawId.includes("_") ? rawId.split("_")[1] : rawId;

    const success = addCartEvent(1);

    if (success) {
      triggerAddToCart({
        item_id: cleanId,
        item_name:
          props?.data?.pDetails?.title || props?.data?.pDetails?.productName,
        item_price: getTotalPrice(props.data.pDetails.price),
        item_count: 1,
        item_size: getSizeFromPreference(selectedPreferenceDetail, selectedValue),
        store_name: shop?.name,
        store_id: props?.data?.pDetails?.storeInfo?._id,
        movement_type: shop?.movementType?.value || "",
        sector: shop?.sector?.value || "",
        terminal: shop?.terminal?.value || "",
        domain: shop?.domain || "",
        fulfilment_type:
          useDelivery?.selectedDeliveryOption?.deliveryOption ===
            config?.deliveryOptions?.deliveryAtGate
            ? config?.productFulfilmentType?.delivery
            : config?.productFulfilmentType?.takeaway || "",
        delivery_time: useDelivery?.selectedDeliveryOption?.deliveryTime || "",
        delivery_time_ms: useDelivery?.selectedDeliveryOption?.deliveryTime
          ? new Date(useDelivery.selectedDeliveryOption.deliveryTime).getTime()
          : 0,
        preference_available: props?.data?.pDetails?.itemWithPreference,
        preference_selected:
          getTitleArrayFromPreference(selectedPreferenceDetail) || [],
        preference_price:
          getPriceArrayFromPreference(selectedPreferenceDetail) || [],
        trigger_point: "Product Details Modal",
      });
    }
  };

  const goToCart = () => {
    if (isLoggedIn()) {
      history.push({ pathname: "/cart" });
    } else {
      history.push({ pathname: "/signup" });
    }
  };

  function handleAddonsChange(addonItem) {
    let subData = addOnes;
    for (let data of subData) {
      if (data.addonId == addonItem.addonId) {
        data.selected = data.selected === null ? true : !data.selected;
      }
    }
    setAddOnes([...subData]);

    let selectedAddOns = [];
    addOnes?.forEach((x) => {
      if (x.selected) {
        selectedAddOns.push(x);
      }
    });
  }

  const getSelectedAddOnPrice = (items = []) =>
    items.reduce((total, item) => {
      if (item.selected === false) return total;
      return total + Number(item.unitPrice || item.price || 0);
    }, 0);

  const getDetailedPreferencePrice = (preferences = []) =>
    preferences.reduce((total, preference) => {
      return (
        total +
        (preference.selection || []).reduce((selectionTotal, item) => {
          if (item?.subPreferences?.length > 0) {
            return (
              selectionTotal +
              item.subPreferences.reduce((subTotal, subPref) => {
                return (
                  subTotal +
                  (subPref.subPreferenceValues || []).reduce(
                    (valueTotal, subItem) =>
                      valueTotal + Number(subItem.price || subItem.pricing || 0),
                    0
                  )
                );
              }, 0)
            );
          }

          return selectionTotal + Number(item.price || item.pricing || 0);
        }, 0)
      );
    }, 0);

  const isSameSelectedAttributes = (cartItem) => {
    if (!props.isEdit || !cartItem?.customisationAttributes?.length) {
      return false;
    }

    return cartItem.customisationAttributes.every((attr) => {
      const currentValue = selectedValue?.[attr.attributeName]?.[0];
      const cartValue = attr.value?.[0];

      return currentValue && cartValue && currentValue === cartValue;
    });
  };

  const getProductPrice = (productPrice) => {
    const cartItem = props.feGenId
      ? useCart.getItems().find((item) => item.feGenId === props.feGenId)
      : null;

    if (
      !hasVariantChanged &&
      cartItem?.itemPrice &&
      isSameSelectedAttributes(cartItem)
    ) {
      return (
        Number(cartItem.itemPrice) -
        getSelectedAddOnPrice(cartItem.addOn || []) -
        getDetailedPreferencePrice(cartItem.customisationPreferences || [])
      );
    }

    return Number(productPrice || 0);
  };

  function getTotalPrice(productPrice) {
    let val = 0;
    let addOnPrice = 0;
    let preferencePrice = 0;

    addOnes?.forEach((x) => {
      if (x.selected) {
        addOnPrice += x.unitPrice;
      }
    });

    const collectSelectedPrices = (selectedEntries, prefTree) => {
      (selectedEntries || []).forEach((selectedEntry) => {
        const matchedGroup = (prefTree || []).find(
          (g) =>
            String(g.preferenceId) === String(selectedEntry.preferenceId)
        );

        if (!matchedGroup) return;

        (selectedEntry.values || []).forEach((selectedVal) => {
          const matchedItem = (matchedGroup.preferenceValues || []).find(
            (pv) =>
              String(pv.preferenceSku) ===
              String(selectedVal.preferenceValueId)
          );

          if (!matchedItem) return;

          const unitPrice =
            matchedItem.pricing || matchedItem.price || 0;

          const quantity = selectedVal.quantity || 1;

          preferencePrice += unitPrice * quantity;

          if (selectedVal.subPreferences?.length) {
            const nextPrefTree = matchedItem.subPreferences || [];

            collectSelectedPrices(
              selectedVal.subPreferences,
              nextPrefTree
            );
          }
        });
      });
    };
    collectSelectedPrices(
      selectedPreferences,
      props?.data?.pDetails?.preferences || []
    );

    val =
      getProductPrice(productPrice) +
      addOnPrice +
      preferencePrice;

    return val;
  }

  const checkMandatorySelections = (updatedNestedPreferences) => {
    if (props?.data?.masterVariants?.productVariants?.length > 0) {
      if (selectedValue && Object.keys(selectedValue).length === 0) {
        const variantElement =
          preferencesRefs.current[
          props.data?.masterVariants?.productVariants[0]?.attributeName
          ];
        if (variantElement) {
          variantElement.scrollIntoView({ behavior: "smooth" });
          variantElement.classList.add("text-red-500");
          setTimeout(() => {
            variantElement.classList.remove("text-red-500");
          }, 1000);
        }
        setCustomizedError(
          "Please select at least one option from the customisation options."
        );
        return false;
      }
    }
    const mandatoryPreferenceIds = new Set(
      props?.data?.pDetails?.preferences
        .filter((item) => item.isMandatory)
        .map((item) => item.preferenceId)
    );

    const selectedIds = getPreferenceIds(updatedNestedPreferences);

    const allMandatorySelected = [...mandatoryPreferenceIds].every((id) => {
      const preference = props?.data?.pDetails?.preferences?.find(
        (item) => item.preferenceId === id
      );

      if (preference?.subPreferences?.length > 0) {
        return preference.subPreferences.some((subPref) =>
          selectedIds.includes(subPref.preferenceId)
        );
      }

      return selectedIds.includes(id);
    });

    if (allMandatorySelected) {
      setCustomizedError();
    } else {
      const firstUnselectedId = [...mandatoryPreferenceIds].find(
        (id) => !selectedIds.includes(id)
      );

      const prefElement = preferencesRefs.current[firstUnselectedId];

      if (prefElement) {
        prefElement.scrollIntoView({ behavior: "smooth" });
        prefElement.classList.add("text-red-500");

        setTimeout(() => {
          prefElement.classList.remove("text-red-500");
        }, 1000);
      }

      setCustomizedError(
        "Please select all mandatory items from the customisation options."
      );
    }

    return allMandatorySelected;
  };


  const getPreferenceIds = (preferences) => {

    const ids = [];

    const traverse = (items) => {
      items.forEach((item) => {
        ids.push(item.preferenceId);

        item.values?.forEach((value) => {
          if (value.subPreferences?.length) {
            traverse(value.subPreferences);
          }
        });
      });
    };

    traverse(preferences);

    return ids;
  };

  const addCartEvent = (qty) => {
    if (
      props?.data?.masterVariants?.ageVerificationRequired &&
      !getSessionStorage("isAgeDeclared")
    ) {
      setShowAgeVerificationPrompt(true);
      return false;
    }

    const checkMinMaxPrefrenceSelection = checkMinMaxPreference(
      selectedPreferences,
      props?.data?.pDetails?.preferences
    );
    if (!checkMinMaxPrefrenceSelection?.isCheckMinMaxPreference) {
      setCustomizedError(
        checkMinMaxPrefrenceSelection?.checkMinMaxErr || "",
      );
      setTimeout(() => {
        setCustomizedError("");
      }, 3000);
      return false;
    }
    const fullPreferenceTree = props?.data?.pDetails?.preferences || [];
    const updatedPreferences = buildSelectedStructure(selectedPreferences, fullPreferenceTree);
    const isMandatorySelected = checkMandatorySelections(updatedPreferences);
    if (!isMandatorySelected) {
      return false;
    }
    let selectedAddOns = [];
    addOnes?.forEach((x) => {
      if (x.selected) {
        selectedAddOns.push(x);
      }
    });

    let mandatoryPrefSel = true;
    let attributeList = [];
    if (props?.data?.masterVariants?.productVariants) {
      attributeList = JSON.parse(
        JSON.stringify(props?.data?.masterVariants?.productVariants)
      );
    }
    attributeList.forEach((attr) => {
      if (selectedValue[attr.attributeName]) {
        attr.value = selectedValue[attr.attributeName];
      } else {
        attr.value = [];
      }
    });

    props?.data?.masterVariants?.productVariants?.forEach((masVar) => {
      let found = attributeList.find(
        (check) => check.attributeName === masVar.attributeName
      );
      if (found && found.value && found.value.length === 0) {
        mandatoryPrefSel = false;
      }
    });

    if (!mandatoryPrefSel) {
      setErr("Please select all mandatory preferences");
      setTimeout(() => {
        setErr("");
      }, 2000);
      return false;
    }

    const itemId = props.data.pDetails._id;
    const itemQuantity = qty;
    const itemType = props.data.pDetails.type;
    const domain = props.data.pDetails.domain;
    const addOn = selectedAddOns;
    const deliveryOptions = useDelivery.selectedDeliveryOption;

    let fulfilmentType = config?.productFulfilmentType?.takeaway || "";

    const data = JSON.parse(localStorage.getItem(DATA_REFERENCE));
    const terminal =
      data && data[TERMINAL_KEY]
        ? data[TERMINAL_KEY]
        : props?.shopDetails?.terminal?.value ||
        props?.data?.pDetails?.storeInfo?.terminal?.value ||
        "";
    const sector =
      data && data[SECTOR_KEY]
        ? data[SECTOR_KEY]
        : props?.shopDetails?.sector?.value ||
        props?.data?.pDetails?.storeInfo?.sector?.value ||
        "";
    const movementType =
      data && data[MOVEMENT_KEY]
        ? data[MOVEMENT_KEY]
        : props?.shopDetails?.movementType?.value ||
        props?.data?.pDetails?.storeInfo?.movementType?.value ||
        "";

    if (
      deliveryOptions?.deliveryOption === config.deliveryOptions.deliveryAtGate
    ) {
      fulfilmentType = config.productFulfilmentType.delivery;
    }

    let pVar = [];
    props.data?.pVariables?.map((x) => {
      if (x._id) {
        pVar.push(x._id);
      }
    });
  

    const customisationPreferences = buildSelectedCustomizationStructure(selectedPreferences, fullPreferenceTree);

    let cartItem = useCart.getItem({
      storecode: props.data.pDetails.shopId,
      productId: itemId,
      addOn: addOn,
      customisationPreferences: customisationPreferences,
    });
    if (cartItem) {
      const updateSuccessful = updateCartEvent(cartItem.itemQuantity + 1);
      if (!updateSuccessful) return;
    } else {
      let addParam = {
        storecode: props.data.pDetails.shopId,
        storeName: props.data.pDetails.shopName,
        movementType: movementType,
        sector: sector,
        terminal: terminal,
        domain,
        fulfilmentType: fulfilmentType,
        item: {
          storeName: props.data.pDetails.shopName,
          storeImage: props.data.pDetails.shopImage,
          storeId: props.data.pDetails.shopId,
          productSKU: "",
          itemId,
          itemName:
            props?.data?.pDetails?.title || props?.data?.pDetails?.productName,
          itemPrice: getTotalPrice(props.data.pDetails.price),
          itemQuantity,
          itemType,
          addOn,
          preferences: updatedPreferences,
          customisationPreferences: customisationPreferences,
          customisationAttributes: attributeList,
          deliveryOptions,
          flightId: "",
          flightUid: "",
          dietCategory: props.data.pDetails.dietCategory || "",
          productImage: props.data.pDetails.productImageUrl,
          feGenId: makeUnique(5),
          pVariables: pVar || [],
        },
      };

      if (useCart.addItem(addParam)) {
        setSelectedPreferences([]);
        setSelectedPreferenceDetail([]);
      }
      props.handleSubmit && props.handleSubmit(true, itemQuantity);
    }
    handleModalClose();
    if (props?.data?.recommendation) {
      props.setRecommendationList &&
        props.setRecommendationList(props?.data?.recommendation);
      props.setRecommendationModal && props.setRecommendationModal(true);
    }
    return true;
  };

  function makeUnique(base) {
    var now = new Date().getTime();
    var random = Math.floor(Math.random() * 100000);
    random = "" + random;
    while (random.length < 5) {
      random = "0" + random;
    }
    return base + now + random;
  }

  const updateCartEvent = (qty, isUpdate) => {
    let selectedAddOns = [];
    addOnes?.forEach((x) => {
      if (x.selected) {
        selectedAddOns.push(x);
      }
    });

    let mandatoryPrefSel = true;
    let attributeList = [];
    if (props?.data?.masterVariants?.productVariants) {
      attributeList = JSON.parse(
        JSON.stringify(props?.data?.masterVariants?.productVariants)
      );
    }
    attributeList.forEach((attr) => {
      if (selectedValue[attr.attributeName]) {
        attr.value = selectedValue[attr.attributeName];
      } else {
        attr.value = [];
      }
    });

    props?.data?.masterVariants?.productVariants?.forEach((masVar) => {
      let found = attributeList.find(
        (check) => check.attributeName === masVar.attributeName
      );
      if (found && found.value && found.value.length === 0) {
        mandatoryPrefSel = false;
      }
    });

    const checkMinMaxPrefrenceSelection = checkMinMaxPreference(
      selectedPreferences,
      props?.data?.pDetails?.preferences
    );
    if (!checkMinMaxPrefrenceSelection?.isCheckMinMaxPreference) {
      setCustomizedError(
        checkMinMaxPrefrenceSelection?.checkMinMaxErr || "",
      );
      setTimeout(() => {
        setCustomizedError("");
      }, 3000);
      return false;
    }

    const isMandatorySelected = checkMandatorySelections(selectedPreferences);
    if (!isMandatorySelected) {
      return false;
    }

    if (!mandatoryPrefSel) {
      setErr("Please select all mandatory preferences");
      setTimeout(() => {
        setErr("");
      }, 2000);
      return false;
    }

    const itemId = props.data.pDetails._id;
    let itemQuantity = qty;
    const itemType = props.data.pDetails.type;
    const addOn = selectedAddOns;
    const deliveryOptions = useDelivery.selectedDeliveryOption;
    const fullPreferenceTree = props?.data?.pDetails?.preferences || [];
    const customisationPreferences = buildSelectedCustomizationStructure(selectedPreferences, fullPreferenceTree);

    let cartItem = useCart.getItem({
      storecode: props.data.pDetails.shopId,
      productId: itemId,
      addOn: addOn,
      customisationPreferences: customisationPreferences,
    });

    if (isUpdate && cartItem) {
      setCustomizedError("Item with this combination already exists.");
      return false;
    }

    let itemObj = {};
    useCart.getItems().forEach((x) => {
      if (x.feGenId && x.feGenId === props.feGenId) {
        itemObj = x;
      }
    });

    if (itemObj && Object.keys(itemObj).length > 0) {
      itemQuantity = isUpdate ? itemObj.itemQuantity : qty;
    }

    let updateParam = {
      storecode: props.data.pDetails.shopId,
      storeName: props.data.pDetails.shopName,
      item: {
        feGenId: props.feGenId,
        storeName: props.data.pDetails.shopName,
        storeImage: props.data.pDetails.shopImage,
        storeId: props.data.pDetails.shopId,
        productSKU: "",
        itemId,
        itemName:
          props?.data?.pDetails?.title || props?.data?.pDetails?.productName,
        itemPrice: getTotalPrice(props.data.pDetails.price),
        itemQuantity,
        itemType,
        addOn,
        preferences: selectedPreferences,
        customisationPreferences: customisationPreferences,
        customisationAttributes: attributeList,
        deliveryOptions,
        flightId: "",
        flightUid: "",
        dietCategory: props.data.pDetails.dietCategory || "",
        productImage: props.data.pDetails.productImageUrl,
      },
      isEdit: props.isEdit,
    };
    useCart.updateCompleteItem(updateParam);
    setSelectedPreferences([]);
    setSelectedPreferenceDetail([]);
    props.handleSubmit && props.handleSubmit(true, itemQuantity);
    if (props.setEditMode) {
      props.setEditMode(true);
    }

    handleModalClose();
    return true;
  };

  const isSelected = (preferenceId, sku) => {
    const findPref = (entries) => {
      for (const entry of (entries || [])) {
        if (String(entry.preferenceId) === String(preferenceId)) {
          return entry;
        }
        for (const val of (entry.values || [])) {
          for (const sub of (val.subPreferences || [])) {
            const found = findPref([sub]);
            if (found) return found;
          }
        }
      }
      return null;
    };

    const pref = findPref(selectedPreferences);
    if (!pref) return false;

    const searchValues = (values) => {
      if (!Array.isArray(values)) return false;
      return values.some((v) => {
        if (String(v.preferenceValueId) === String(sku)) return true;
        if (Array.isArray(v.subPreferences)) {
          return v.subPreferences.some((subGroup) =>
            searchValues(subGroup.values)
          );
        }
        return false;
      });
    };

    return searchValues(pref.values);
  };

  const getItemPreferenceTitle = (preference) => {
    if (!preference) return "";

    const {
      isMandatory,
      minPrefPermitted,
      maxPrefPermitted,
      singleSelect,
      preferenceValues = [],
    } = preference;

    const hasMin = minPrefPermitted !== undefined && minPrefPermitted !== null;
    const hasMax = maxPrefPermitted !== undefined && maxPrefPermitted !== null;
    const isLimited = maxPrefPermitted !== preferenceValues.length;

    let text = "";

    if (hasMin && hasMax && !singleSelect) {
      const hasMinLimit = minPrefPermitted > 0;

      if (hasMinLimit && isLimited) {
        text = `[select ${minPrefPermitted} - ${maxPrefPermitted}]`;
      } else if (hasMinLimit) {
        text = `[select at least ${minPrefPermitted}]`;
      } else if (isLimited) {
        text = `[select up to ${maxPrefPermitted}]`;
      }
    }

    if (isMandatory) {
      text = text ? `[Required] ${text} ` : `[Required]`;
    }

    return text;
  };

  const checkMinMaxPreference = (selectedPreference, itemPreferences) => {
    let checkPreference = {
      isCheckMinMaxPreference: true,
      checkMinMaxErr: "",
    };

    const validateGroups = (groups, selectedEntries) => {
      for (const preference of groups || []) {
        const entry = (selectedEntries || []).find(
          (e) => String(e.preferenceId) === String(preference?.preferenceId)
        );
        const selectedValues = entry?.values || [];
        const minPrefPermitted = preference?.minPrefPermitted;

        if (
          !preference?.singleSelect &&
          minPrefPermitted > 0 &&
          selectedValues.length < minPrefPermitted
        ) {
          checkPreference.isCheckMinMaxPreference = false;
          checkPreference.checkMinMaxErr = `You must choose at least ${minPrefPermitted} options.`;
          return false;
        }

        for (const value of selectedValues) {
          const option = (preference?.preferenceValues || []).find(
            (v) => String(v.preferenceSku) === String(value.preferenceValueId)
          );
          if (!validateGroups(option?.subPreferences, value.subPreferences)) {
            return false;
          }
        }
      }
      return true;
    };

    validateGroups(itemPreferences, selectedPreference);

    return checkPreference;
  };

  const toggleSubPref = (key) => {
    setOpenSubPref((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };


  const renderPreferenceItem = (item, index, parentKey, preference, depth = 0, isRadio, parentPreferenceItem = null, singleSelectForLevel) => {
    const key = `${parentKey}-${String(index)}`;
    const hasNested = item?.subPreferences?.length > 0;
    const isExpanded = !!openSubPref[key];
    const isPreferenceSelected = isSelected(preference.preferenceId, item.preferenceSku);
    const preferenceQtyChecked = !hasNested && isPreferenceSelected && item?.repeatable;

    return (
      <div key={key} className={styles.preference_card_outer}>
        <div
          className={depth === 0 ? styles.preference_selection_card : styles.grouped_row}
          style={{ paddingLeft: depth > 0 ? "36px" : "20px" }}
          onClick={() => {
            if (hasNested) {
              toggleSubPref(key);
            } else {
              handlePreferenceSelection(
                preference.preferenceId,
                preference.type,
                preference.singleSelect,
                {
                  ...item,
                  isSubPreferenceSelected: depth > 0,
                  parentPreferenceSku: depth > 0 ? parentPreferenceItem?.preferenceSku : undefined,
                },
                preference,
                depth > 0 ? parentPreferenceItem : null,
                depth > 0
              );
            }
          }}
        >
          <div className={styles.card_title}>
            {hasNested && depth > 0 && (
              <Arrow src={ArrowNestedIcon} isOpen={isExpanded} type="left" style={{ marginRight: "8px" }} />
            )}
            <div className={styles.card_title_wrapper}>
              {hasNested ? null : singleSelectForLevel ? (
                <Radio
                  checked={isSelected(preference.preferenceId, item.preferenceSku)}
                  value={item.name}
                  sx={{
                    color: "#999",
                    "&.Mui-checked": { color: "var(--primary)" },
                    "& .MuiSvgIcon-root": { fontSize: isTab() ? 20 : 32 },
                  }}
                />
              ) : (
                <CustomCheckbox checked={isSelected(preference.preferenceId, item.preferenceSku)} />
              )}
              <Text>{item.name && item.name.toLowerCase()}</Text>
            </div>
          </div>

          <div className={styles.card_radio_button_container}>
            {(item.price || item.pricing) > 0 && (
              <div className={styles.card_price}>
                <Text>+{formatPrice(item.price || item.pricing)}</Text>
              </div>
            )}

            {preferenceQtyChecked && <div className={styles.preference_qty_wrapper}>
              <div className={styles.preference_btn}>
                <div
                  className={styles.button_icon_wrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreferenceSelection(
                      preference.preferenceId,
                      preference.type,
                      preference.singleSelect,
                      {
                        ...item,
                        isSubPreferenceSelected: depth > 0,
                        parentPreferenceSku: depth > 0 ? parentPreferenceItem?.preferenceSku : undefined,
                      },
                      preference,
                      depth > 0 ? parentPreferenceItem : null,
                      depth > 0,
                      fetchSelectedPreferenceQty(selectedPreferences, item) - 1
                    );
                  }}
                >
                  <img
                    className={styles.item_qty_icon}
                    src={minusBlackIcon}
                    alt="decrease"
                  />
                </div>

                <div
                  className={styles.item_quantity_wrapper}
                >
                  <Text type="semi-bold">{fetchSelectedPreferenceQty(selectedPreferences, item)}</Text>
                </div>
                <div
                  className={styles.button_icon_wrapper}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreferenceSelection(
                      preference.preferenceId,
                      preference.type,
                      preference.singleSelect,
                      {
                        ...item,
                        isSubPreferenceSelected: depth > 0,
                        parentPreferenceSku: depth > 0 ? parentPreferenceItem?.preferenceSku : undefined,
                      },
                      preference,
                      depth > 0 ? parentPreferenceItem : null,
                      depth > 0,
                      fetchSelectedPreferenceQty(selectedPreferences, item) + 1
                    );
                  }}
                >
                  <img
                    className={styles.item_qty_icon}
                    src={plusBlackIcon}
                    alt="increase"
                  />
                </div>
              </div>
            </div>}

            {hasNested && depth === 0 && (
              <Arrow src={ArrowNestedIcon} isOpen={isExpanded} type="right" />
            )}
          </div>
        </div>

        {hasNested && isExpanded && (
          <div className={styles.same_box_children}>
            {item.subPreferences.flatMap((subPrefGroup, subIndex) =>
              (subPrefGroup?.preferenceValues ?? []).map((nestedItem, nIndex) =>
                renderPreferenceItem(
                  nestedItem,
                  nIndex,
                  `${key}-${subIndex}`,
                  subPrefGroup,
                  depth + 1,
                  isRadio,
                  parentPreferenceItem,
                  subPrefGroup?.singleSelect
                )
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const renderGroupedRow = (item, index, parentKey, preference, isRadio, isLast, parentPreferenceItem = null) => {
    const key = `${parentKey}-${String(index)}`;
    const hasNested = item?.subPreferences?.length > 0;

    if (hasNested) {
      const isExpanded = !!openSubPref[key];
      return (
        <div key={key}>
          <div
            className={styles.grouped_row}
            style={{ borderBottom: isLast && !isExpanded ? "none" : undefined }}
            onClick={() => toggleSubPref(key)}
          >
            <Text>{item.name && item.name.toLowerCase()}</Text>
            <Arrow src={ArrowNestedIcon} isOpen={isExpanded} type="right" />
          </div>
          {isExpanded && (
            <div className={styles.grouped_options_box_nested}>
              {item.subPreferences.flatMap((subPrefGroup, subIndex) =>
                (subPrefGroup?.preferenceValues ?? []).map((nestedItem, nIndex, arr) =>
                  renderGroupedRow(
                    nestedItem,
                    nIndex,
                    `${key}-${subIndex}`,
                    subPrefGroup,
                    isRadio,
                    nIndex === arr.length - 1,
                    item
                  )
                )
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={key}
        className={styles.grouped_row}
        style={{ borderBottom: isLast ? "none" : undefined }}
        onClick={() =>
          handlePreferenceSelection(
            preference.preferenceId,
            preference.type,
            preference.singleSelect,
            {
              ...item,
              isSubPreferenceSelected: true,
              parentPreferenceSku: parentPreferenceItem?.preferenceSku ?? preference?.preferenceSku,
            },
            preference,
            parentPreferenceItem ?? preference,
            true
          )
        }
      >
        <Text>{item.name && item.name.toLowerCase()}</Text>
        <div className={styles.card_radio_button_container}>
          {(item.price || item.pricing) > 0 && (
            <div className={styles.card_price}>
              <Text>+{formatPrice(item.price || item.pricing)}</Text>
            </div>
          )}
          {isRadio ? (
            <Radio
              checked={isSelected(preference.preferenceId, item.preferenceSku)}
              value={item.name}
              sx={{
                color: "#999",
                "&.Mui-checked": { color: "var(--primary)" },
                "& .MuiSvgIcon-root": { fontSize: isTab() ? 20 : 32 },
              }}
            />
          ) : (
            <CustomCheckbox checked={isSelected(preference.preferenceId, item.preferenceSku)} />
          )}
        </div>
      </div>
    );
  };
  const flattenSelections = (groups = []) => {
    const flattened = [];

    const traverse = (group) => {
      if (!group) return;

      (group.values || []).forEach((value) => {
        flattened.push(value);

        (value.subPreferences || []).forEach((subGroup) => {
          traverse(subGroup);
        });
      });
    };

    groups.forEach(traverse);

    return flattened;
  };

  const fetchSelectedPreferenceQty = (selectedItems, item) => {
    const flatSelectedItems = flattenSelections(selectedItems);

    const matched = flatSelectedItems.find(
      (selected) =>
        String(selected.preferenceValueId) === String(item.preferenceSku)
    );

    return matched?.quantity ?? 1;
  };
  if (isModalLoading) {
    return (
      <Modal open={props.open} onClose={handleModalClose}>
        <Box sx={style}>
          <div className={styles.details_modal_container}>
            <Loader height="100%" />
          </div>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={props.open} onClose={handleModalClose}>
        <Box sx={style}>
          <div className={styles.details_modal_container}>
            <div className={styles.close_button_container}>
              <div className={styles.close_button_wrapper}>
                <Image
                  src={XIconBlack}
                  style={{ width: "100%", height: "100%" }}
                  onClick={handleModalClose}
                />
              </div>
            </div>
            {props.data && Object.keys(props.data).length > 0 ? (
              <div className={styles.modal_content_wrapper}>
                <div className={styles.modal_content_container}>
                  <div className={styles.modal_title_text}>
                    <Text variant="header">Customise your order</Text>
                  </div>
                  <div className={styles.meta_details_container}>
                    <div className={styles.product_image_container}>
                      <ProgressiveImg
                        src={getProductImage()}
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "8px",
                          objectFit: "inherit",
                        }}
                      />
                    </div>
                    <div className={styles.product_data_container}>
                      <div className="">
                        <div className={styles.product_name}>
                          <div className={styles.product_name_wrapper}>
                            <Text>
                              {props?.data?.pDetails?.title ||
                                props?.data?.pDetails?.productName}
                            </Text>
                          </div>
                        </div>
                        {props.data.pDetails &&
                          props.data.pDetails.description && (
                            <>
                              <div
                                ref={descRef}
                                className={styles.product_desc}
                                style={{
                                  "-webkit-line-clamp": showFullDesc
                                    ? "1000"
                                    : "2",
                                }}
                              >
                                <Text>
                                  {parse(
                                    props?.data?.pDetails?.description
                                      ?.longDescription ||
                                    props?.data?.pDetails?.description
                                      ?.shortDescription ||
                                    ""
                                  )}
                                </Text>
                              </div>
                              <div>
                                {showReadMore ? (
                                  <div className={styles.read_more_less_text}>
                                    <Text
                                      onClick={() =>
                                        setShowFullDesc(!showFullDesc)
                                      }
                                    >
                                      {showFullDesc ? "Read Less" : "Read More"}
                                    </Text>
                                  </div>
                                ) : null}
                              </div>
                            </>
                          )}
                      </div>
                      {props?.data?.pDetails?.price > 0 && (
                        <div className={styles.product_price}>
                          <Text>
                            {props?.data?.pDetails?.itemWithPreference &&
                              isValidPrice(props?.data?.pDetails?.priceRange?.minPrice) &&
                              isValidPrice(props?.data?.pDetails?.priceRange?.maxPrice) &&
                              props?.data?.pDetails?.priceRange?.minPrice !==
                              props?.data?.pDetails?.priceRange?.maxPrice
                              ? `${formatPrice(props.data.pDetails.priceRange.minPrice)} - ${formatPrice(
                                props.data.pDetails.priceRange.maxPrice
                              )}`
                              : formatPrice(getProductPrice(props?.data?.pDetails?.price))}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                  {err && err !== "" && (
                    <div className={styles.error_text}>
                      <Text>Please select all mandatory preferences</Text>
                    </div>
                  )}
                  <div className={styles.preference_selection_container}>
                    {props?.data?.masterVariants?.productVariants?.map(
                      (variantItem, variantIndex) => (
                        <div
                          key={variantIndex}
                          ref={(el) =>
                          (preferencesRefs.current[
                            variantItem.attributeName
                          ] = el)
                          }
                        >
                          <div className={styles.preference_selection_wrapper}>
                            <div className={styles.pref_section_title_wrapper}>
                              <Text style={{ display: "flex" }} variant="label">
                                {variantItem.attributeName} [Required]
                              </Text>
                            </div>
                            <div
                              className={
                                styles.preference_selection_card_wrapper
                              }
                            >
                              {variantItem?.attributeValues?.map(
                                (valueItem, valueIndex) => (
                                  <div
                                    className={styles.preference_selection_card}
                                    key={valueIndex}
                                    onClick={(e) =>
                                      handleAttributeSelection(
                                        variantItem.attributeName,
                                        valueItem
                                      )
                                    }
                                  >
                                    <div className={styles.card_title}>
                                      <div
                                        className={styles.card_title_wrapper}
                                      >
                                        <Text>
                                          {valueItem.attributeValue &&
                                            valueItem.attributeValue.toLowerCase()}
                                        </Text>
                                      </div>
                                    </div>
                                    <div
                                      className={
                                        styles.card_radio_button_container
                                      }
                                    >
                                      {(valueItem.pricing || valueItem.price) >
                                        0 ? (
                                        <div className={styles.card_price}>
                                          <Text>
                                            +
                                            {formatPrice(
                                              valueItem.pricing ||
                                              valueItem.price
                                            )}{" "}
                                          </Text>
                                        </div>
                                      ) : null}
                                      <div
                                        className={
                                          styles.card_radio_button_wrapper
                                        }
                                      >
                                        <Radio
                                          checked={
                                            selectedValue !== undefined &&
                                            selectedValue !== null &&
                                            Object.keys(selectedValue).length >
                                            0 &&
                                            selectedValue[
                                            variantItem.attributeName
                                            ] &&
                                            selectedValue[
                                              variantItem.attributeName
                                            ].includes(valueItem.attributeValue)
                                          }
                                          value={valueItem.attributeValue}
                                          sx={{
                                            color: "#999",
                                            "&.Mui-checked": {
                                              color: "var(--primary)",
                                            },
                                            "& .MuiSvgIcon-root": {
                                              fontSize: isTab() ? 20 : 32,
                                            },
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                          <div className={styles.horz_divider}></div>
                        </div>
                      )
                    )}
                    {props?.data?.pDetails?.preferences?.map(
                      (preferenceItem, prefIndex) => {
                        const isRadio = preferenceItem.singleSelect;

                        return (
                          <div
                            key={preferenceItem.preferenceId}
                            style={{ paddingTop: prefIndex === 0 ? 0 : 24 }}
                            ref={(el) => {
                              if (preferenceItem.isMandatory) {
                                preferencesRefs.current[
                                  preferenceItem.preferenceId
                                ] = el;
                              }
                            }}
                          >
                            <div className={styles.preference_selection_wrapper}>
                              <div className={styles.pref_section_title_wrapper}>
                                <Text variant="label">
                                  {preferenceItem.type}{" "}
                                  {getItemPreferenceTitle(preferenceItem)}
                                </Text>
                              </div>
                            </div>

                            <div className={styles.preference_selection_card_wrapper}>
                              {preferenceItem.preferenceValues.map((item, index) =>
                                renderPreferenceItem(
                                  item,
                                  index,
                                  `${preferenceItem.preferenceId}`,
                                  preferenceItem,
                                  0,
                                  isRadio,
                                  props?.data?.pDetails?.preferences,
                                  preferenceItem?.singleSelect
                                )
                              )}
                            </div>
                            <div className={styles.horz_divider}></div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
                <div className={styles.add_to_cart_band}>
                  {customizedError && (
                    <ErrorWrapper>
                      <ErrorText>
                        <Text>{customizedError}</Text>
                      </ErrorText>
                    </ErrorWrapper>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <div className={styles.action_container}>
                      <Button
                        style={{
                          backgroundColor: "var(--primary)",
                          border: "none",
                          padding: isJFK() ? "16px 32px" : "24px",
                        }}
                        disabled={isLoading}
                        onClick={() => {
                          if (isLoading) return;
                          props.isEdit
                            ? updateCartEvent(1, true)
                            : handleAddItem();
                        }}
                      >
                        <div className={styles.modal_confirm_btn_label}>
                          <Text fontFamily="var(--font-secondary)">
                            {props.isEdit ? "Update Item" : "Add to cart"}
                            {!isLoading &&
                              (addOnes?.length > 0 ||
                                Object.keys(selectedValue)?.length > 0 ||
                                selectedPreferences?.length > 0) && (
                                <>
                                  {" "}
                                  |{" "}
                                  {formatPrice(
                                    parseFloat(
                                      getTotalPrice(props.data.pDetails.price)
                                    ).toLocaleString("en", {
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits:
                                        parseFloat(
                                          getTotalPrice(
                                            props.data.pDetails.price
                                          )
                                        ) %
                                          1 ===
                                          0
                                          ? 0
                                          : 2,
                                    })
                                  )}
                                </>
                              )}
                          </Text>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Loader height="100%" />
            )}
          </div>
        </Box>
      </Modal>
      {showAgeVerificationPrompt && (
        <DisclaimerPrompt
          isDrawerOpen={showAgeVerificationPrompt}
          setIsDrawerOpen={setShowAgeVerificationPrompt}
          successHandler={(e) => addCartEvent(1)}
        ></DisclaimerPrompt>
      )}
    </>
  );
}

export default ProductDetailsModal;

const ErrorWrapper = styled.div`
  left: 40px;
  right: 40px;
  top: 12px;
  margin-bottom: 24px;
`;

const ErrorText = styled.div`
  font-size: 24px;
  line-height: 19px;
  padding-top: 4px;
  color: #b42a2a;
`;

const Arrow = styled.img`
  width: 20px;
  height: 20px;
  transition: transform 0.25s ease;
  transform: ${({ isOpen }) => (isOpen ? "rotate(0deg)" : "rotate(180deg)")};
`;
